import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  useNavigate, useParams, useSearchParams, useBlocker,
  UNSAFE_DataRouterContext as DataRouterContext,
} from 'react-router-dom';
import { projectGridService } from '../../services/api';
import { getMyProjectGrid } from '../../api/myProjectsApi';
import { fmtD, parseDmy, addBD, bdBetween, buildRows, descendants, recalcDates, normalizeToBusinessDay, toLocalIsoDate, parseDateLocal } from './planUtils';
import GanttView from './GanttView';
import UnsavedChangesDialog from './UnsavedChangesDialog';
import ProjectPropertiesDrawer from './ProjectPropertiesDrawer';
// QLOG-S2.1 §A — segmented Delivery ↔ Plan toggle in the header.
import ProjectViewToggle from './ProjectViewToggle';
// RA-S5 (2026-07-17) — ProjectAssignmentDrawer + ManageAssignmentsDrawer
// retired with the whole personal-assignment layer. ManageTeamDrawer
// (project consultant roster) survives.
import ManageTeamDrawer from './ManageTeamDrawer';
import { usePermissions } from '../PermissionsContext';
// UX-S1 — in-app confirm dialog replaces the native browser one.
import { useConfirm } from '../Common/ConfirmDialog';
// RA-S5 — CellTooltip retired (assignment-only hover preview).
import EffortGridPrefs, { loadEffortPrefs, saveEffortPrefs } from './EffortGridPrefs';
import './ProjectAssignments.css';
import { ProjectSessionProvider, useProjectSession } from './ProjectSessionContext';
import {
  updateTaskField, setTaskFieldLocal, addTask, addTaskLocal, deleteTask, deleteTaskLocal, reorderTasks,
  addRole, updateRoleField, deleteRole, updateProjectMetadata,
  discard, applySaveResult, buildSavePayload, buildSaveChunks, deserializeSession,
} from './projectSession';
import './ProjectDetailPage.css';

const SESSION_BACKUP_KEY = (id) => `hrapp.project.session.${id}`;

// CTX-MENU-VIEWPORT-FIX-S1 — shared helper for the ctx / mdrop menus.
// Measures the rendered floating element and repositions it so its full
// box fits inside the viewport: flip upward when there isn't room below,
// clamp to the top margin when the flipped position would still overflow,
// clamp horizontally to the right margin. Writes top/left imperatively —
// keeping the CSS's position:fixed and cheap enough to run inside a
// useLayoutEffect after paint-blocking layout.
function clampIntoViewport(el, x, y) {
  const r = el.getBoundingClientRect();
  const M = 8; // viewport margin
  let top = y;
  let left = x;
  if (y + r.height > window.innerHeight - M) {
    top = Math.max(M, y - r.height);
    if (top + r.height > window.innerHeight - M) {
      top = Math.max(M, window.innerHeight - r.height - M);
    }
  }
  if (x + r.width > window.innerWidth - M) {
    left = Math.max(M, window.innerWidth - r.width - M);
  }
  el.style.top = `${top}px`;
  el.style.left = `${left}px`;
}

// LF-S5: bridges react-router's useBlocker into the component. Rendered only
// inside a data router (App uses createBrowserRouter); plain MemoryRouter test
// harnesses skip it, so useBlocker is never called without a data router.
function NavigationBlocker({ active, onBlocked }) {
  const blocker = useBlocker(active);
  useEffect(() => {
    if (blocker.state === 'blocked') onBlocked(blocker);
  }, [blocker, onBlocked]);
  return null;
}

const STATUS_COLORS = {
  Draft: { bg: '#e0e0e0', color: '#555' },
  Active: { bg: '#c8e6c9', color: '#2e7d32' },
  OnHold: { bg: '#fff3e0', color: '#e65100' },
  Completed: { bg: '#bbdefb', color: '#1565c0' },
  Cancelled: { bg: '#ffcdd2', color: '#c62828' },
};

// (fmtD, parseDmy, bdBetween, buildRows, descendants, recalcDates imported from planUtils)
const fmtC = (v) => {
  if (!v || v === 0) return '0.00 €';
  return v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};
// GR-S3: Format hours — round to 2 decimals, strip trailing zeros, append 'h'.
// Returns '' for falsy / zero input (matches the grid's "hide zero" rule) so
// per-row + summed values don't leak IEEE-754 noise like `144.67000000000002h`.
const fmtH = (v) => {
  if (!v || v === 0) return '';
  const rounded = Math.round(v * 100) / 100;
  let s = rounded.toFixed(2);
  s = s.replace(/\.?0+$/, '');
  return `${s}h`;
};

// NF-S2 REV 1.5 — "Красимир Дончев" → "К. Дончев". Read-only role cell
// renders each assignee as `${initial}. ${rest} (${hours}h)` on one line.
const abbrevName = (full) => {
  const s = String(full || '').trim();
  if (!s) return '';
  const sp = s.indexOf(' ');
  if (sp <= 0) return s;
  const first = s.slice(0, sp);
  const rest = s.slice(sp + 1).trim();
  if (!rest) return first;
  return `${first.charAt(0)}. ${rest}`;
};

// ── Cost calculators ───────────────────────────────────────────────────
// OFF-BUDGET-TASK-S1 — when `useSell` is true, off-budget rows contribute
// 0 to the SELL budget (no revenue until a CR). On the COST path
// (`useSell` false) off-budget rows still contribute their real cost —
// the PM sees how much we're "in".
const taskCost = (row, role, useSell = true) => {
  if (useSell && row.offBudget) return 0;
  const h = row.roles?.[role.id]?.plannedHours || 0;
  if (!h) return 0;
  const rate = useSell ? role.sellRatePerHour : role.costRatePerHour;
  return h * rate;
};
// WBS-FIX-S1 — WBS descendants are cost-neutral. When a row is a
// costing-summary, the descendant sum must skip every WBS row in the
// span. A costing-leaf row keeps its own value (planUtils.buildRows
// ensures a host-of-only-WBS is no longer flagged isSummaryDerived).
// OFF-BUDGET-TASK-S1 — on the SELL rollup, off-budget descendants are
// also skipped (the approved sell budget stays intact). On the COST
// rollup, off-budget descendants are INCLUDED (real spend).
const sumCost = (rows, i, role, useSell = true) => {
  if (!rows[i].isSummaryDerived) return taskCost(rows[i], role, useSell);
  let s = 0;
  for (const j of descendants(rows, i)) {
    if (useSell && rows[j].offBudget) continue;
    if (!rows[j].isSummaryDerived) s += taskCost(rows[j], role, useSell);
  }
  return s;
};
const sumHours = (rows, i, role) => {
  if (!rows[i].isSummaryDerived) {
    return rows[i].roles?.[role.id]?.plannedHours || 0;
  }
  let s = 0;
  for (const j of descendants(rows, i)) {
    if (!rows[j].isSummaryDerived) {
      s += rows[j].roles?.[role.id]?.plannedHours || 0;
    }
  }
  return s;
};
const sumHoursAllRoles = (rows, i, roles) =>
  roles.reduce((s, r) => s + sumHours(rows, i, r), 0);
const rowTotal = (rows, i, roles, useSell) => roles.reduce((s, r) => s + sumCost(rows, i, r, useSell), 0);

// Visibility based on collapsed state of ancestors
const isVisible = (rows, i, collapsed) => {
  let lv = rows[i].outlineLevel;
  for (let j = i - 1; j >= 0; j--) {
    if (rows[j].outlineLevel < lv) {
      if (rows[j].isSummaryDerived && collapsed.has(rows[j].id)) return false;
      lv = rows[j].outlineLevel;
      if (lv === 0) break;
    }
  }
  return true;
};

// Role catalog (rate card for dual-panel picker)
const ROLE_CATALOG = [
  { abbr: 'PM', name: 'Project Manager', sell: 80, cost: 40 },
  { abbr: 'BA', name: 'Business Analyst', sell: 60, cost: 30 },
  { abbr: 'SA', name: 'Solution Architect', sell: 90, cost: 45 },
  { abbr: 'SD', name: 'Senior Developer', sell: 60, cost: 30 },
  { abbr: 'JD', name: 'Junior Developer', sell: 40, cost: 20 },
  { abbr: 'QA', name: 'QA Engineer', sell: 45, cost: 22.5 },
  { abbr: 'UX', name: 'UX Designer', sell: 55, cost: 27.5 },
];


// ── Outer loader — fetches the project, then mounts the editing session ──
// PP-S1: project creation now happens in ProjectPropertiesModal on the list
// page; this page always loads an existing project from
// /api/projects/{id}/detail-grid (PM) or /api/my-projects/{id} (read-only).
//
// NF-S2 (REVISION 1.2) — `readOnly` flips two things in this loader:
//   1. Use `getMyProjectGrid(projectId)` (financial-free, 403-gated payload).
//   2. Skip the localStorage restore prompt — read-only sessions never dirty.
// Then the SAME ProjectSessionProvider wraps the SAME ProjectDetailGrid with
// `readOnly` threaded through. No parallel page or table is ever rendered.
const ProjectDetailManagePage = ({ readOnly = false }) => {
  const { id } = useParams();
  const projectId = parseInt(id, 10);
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // LF-S5: null = undecided (show restore prompt), 'restore' / 'fresh' = chosen.
  const [restoreChoice, setRestoreChoice] = useState(null);

  const load = useCallback(async () => {
    setRestoreChoice(null);
    if (isNaN(projectId)) { setError('Invalid project id'); setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const data = readOnly
        ? await getMyProjectGrid(projectId)
        : await projectGridService.getDetailGrid(projectId);
      const tasks = [...(data.tasks || [])].sort((a, b) => a.sortOrder - b.sortOrder);
      const version = data.version ?? 0;
      // LF-S5: a localStorage backup is offered for restore only when its
      // loaded version matches the freshly fetched one; otherwise it is stale.
      // NF-S2 REV 1.2: read-only sessions never dirty, so skip backup entirely.
      let backupState = null;
      if (!readOnly) {
        try {
          const raw = localStorage.getItem(SESSION_BACKUP_KEY(projectId));
          if (raw) {
            const parsed = deserializeSession(raw);
            if (parsed?.loadedSnapshot?.version === version) backupState = parsed;
            else localStorage.removeItem(SESSION_BACKUP_KEY(projectId));
          }
        } catch { /* corrupt backup — ignore */ }
      }
      setLoaded({
        snapshot: {
          project: {
            id: data.id, code: data.code, name: data.name,
            clientName: data.clientName, description: data.description,
            currencyCode: data.currencyCode, status: data.status,
            isBillable: data.isBillable ?? true,
            createdAt: data.createdAt, updatedAt: data.updatedAt,
            version,
          },
          tasks,
          roles: data.roles || [],
          version,
        },
        key: Date.now(),
        backupState,
      });
    } catch (e) {
      // NF-S2 REV 1.2: a 403 from /my-projects/{id} means the consultant is
      // not a team member — bounce back to /my-projects with a toast.
      if (readOnly && e?.response?.status === 403) {
        setError("You're not on this project's team.");
        setTimeout(() => navigate('/my-projects', { replace: true }), 1500);
      } else {
        setError(e?.response?.data || e?.message || 'Failed to load project');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId, readOnly, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="pdp"><div className="loading-wrap">Loading project…</div></div>;
  if (error) return <div className="pdp"><div className="error-wrap">{String(error)}</div></div>;
  if (!loaded) return null;

  // LF-S5: restore prompt — a dirty session was backed up last time.
  if (loaded.backupState && restoreChoice === null) {
    return (
      <div className="unsaved-overlay" role="dialog" aria-modal="true" data-testid="restore-prompt">
        <div className="unsaved-dialog">
          <h3>Restore unsaved changes?</h3>
          <p>You have unsaved changes from a previous session on this project.</p>
          <div className="unsaved-actions">
            <button className="btn btn-primary" onClick={() => setRestoreChoice('restore')}>
              Restore
            </button>
            <button
              className="btn"
              onClick={() => {
                try { localStorage.removeItem(SESSION_BACKUP_KEY(projectId)); } catch { /* ignore */ }
                setRestoreChoice('fresh');
              }}
            >
              Discard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ProjectSessionProvider
      key={loaded.key}
      initialSnapshot={loaded.snapshot}
      initialState={restoreChoice === 'restore' ? loaded.backupState : undefined}
    >
      <ProjectDetailGrid
        projectId={projectId}
        reload={load}
        readOnly={readOnly}
      />
    </ProjectSessionProvider>
  );
};

// ── Inner grid — every mutation is a session dispatch; nothing hits the
// network until the user clicks Save (LF-S3). ───────────────────────────
const ProjectDetailGrid = ({
  projectId, reload, readOnly = false,
}) => {
  const navigate = useNavigate();
  const dataRouter = useContext(DataRouterContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'gantt' ? 'gantt' : 'effort';

  // LF-S4/LF-S5: unsaved-changes dialog (route-leave guard) + save/conflict state.
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const blockerRef = useRef(null);

  // PP-S1: edit-properties modal (replaces the inline header inputs).
  const [editOpen, setEditOpen] = useState(false);

  // RA-S5 — assignment drawer / manage-assignments drawer / refresh
  // key / per-cell tooltip / projectAssignments cache retired. Only
  // the Manage Team drawer (project consultant roster) survives.
  const [manageTeamOpen, setManageTeamOpen] = useState(false);
  const { hasPermission } = usePermissions();
  const confirm = useConfirm();

  // AS-S2: per-user effort grid preferences (localStorage).
  const [effortPrefs, setEffortPrefs] = useState(() => loadEffortPrefs());
  const [prefsOpen, setPrefsOpen] = useState(false);
  const prefsAnchorRef = useRef(null);

  const { state, dispatch, isDirty: dirty, dirtyCount: dCount } = useProjectSession();
  const project = state.current.project;
  const roles = state.current.roles;
  const tasks = state.current.tasks;

  // NF-S4 — holiday-aware AUTO date math. Loaded once per mount and
  // threaded into every recalcDates / addBD / bdBetween /
  // normalizeToBusinessDay call site below. A fetch failure (network,
  // 403) leaves the set empty so the planUtils helpers degrade to the
  // existing weekend-only behaviour.
  const [holidays, setHolidays] = useState(() => new Set());
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ getHolidays, toHolidaySet }] = await Promise.all([
          import('../../api/holidaysApi'),
        ]);
        const rows = await getHolidays();
        if (!cancelled) setHolidays(toHolidaySet(rows));
      } catch {
        // Degrade silently to weekend-only behaviour.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // RA-S5 — the AS-S2 assignment/employee pre-fetch (which powered the
  // cell hover tooltip + fill-ratio classes) is retired with the whole
  // personal-assignment layer.


  const handleViewChange = (newView) => {
    const next = new URLSearchParams(searchParams);
    if (newView === 'effort') next.delete('view');
    else next.set('view', newView);
    setSearchParams(next, { replace: false });
  };

  const [collapsed, setCollapsed] = useState(new Set()); // task ids that are collapsed
  // TE-S1: selection stored by task ID (array) — survives reorder/indent/delete.
  const [selectedIds, setSelectedIds] = useState([]);
  // TE-S4: anchor for Shift+click range selection.
  const [lastAnchorId, setLastAnchorId] = useState(null);
  const [ctx, setCtx] = useState({ open: false, x: 0, y: 0 });
  const [modeDrop, setModeDrop] = useState({ open: false, x: 0, y: 0, taskId: null });
  // NF-S5 — Task Details drawer state (open record + working copies).
  const [taskDetails, setTaskDetails] = useState(null);
  const [statusDrop, setStatusDrop] = useState({ open: false, x: 0, y: 0, taskId: null });
  // CTX-MENU-VIEWPORT-FIX-S1 — refs used by the layout effects below to
  // measure each open floating menu and clamp/flip it inside the viewport.
  const ctxRef = useRef(null);
  const modeDropRef = useRef(null);
  const statusDropRef = useRef(null);
  // CTX-MENU-VIEWPORT-FIX-S1 — after a floating fixed-positioned menu
  // opens at the raw click point, measure its box and clamp/flip so the
  // whole menu stays inside the viewport. When it doesn't fit below the
  // cursor we open upward; when it overflows right we shift left. Only
  // top/left are touched imperatively — the CSS (position: fixed) and
  // the initial inline style keep working across re-opens.
  useLayoutEffect(() => {
    if (!ctx.open || !ctxRef.current) return;
    clampIntoViewport(ctxRef.current, ctx.x, ctx.y);
  }, [ctx.open, ctx.x, ctx.y]);
  useLayoutEffect(() => {
    if (!modeDrop.open || !modeDropRef.current) return;
    clampIntoViewport(modeDropRef.current, modeDrop.x, modeDrop.y);
  }, [modeDrop.open, modeDrop.x, modeDrop.y]);
  useLayoutEffect(() => {
    if (!statusDrop.open || !statusDropRef.current) return;
    clampIntoViewport(statusDropRef.current, statusDrop.x, statusDrop.y);
  }, [statusDrop.open, statusDrop.x, statusDrop.y]);
  const [roleDialog, setRoleDialog] = useState({ open: false });
  const [rdAvailable, setRdAvailable] = useState([]);
  const [rdProject, setRdProject] = useState([]);
  const [rdSelLeft, setRdSelLeft] = useState(-1);
  const [rdSelRight, setRdSelRight] = useState(-1);
  const [rdView, setRdView] = useState('panels');
  const [rdForm, setRdForm] = useState({ abbr: '', name: '', sell: '', cost: '', err: '' });
  const [rdEditMode, setRdEditMode] = useState(null);
  const [edit, setEdit] = useState(null); // { taskId, field, value }
  const [snack, setSnack] = useState({ msg: '', err: false });
  const snackTimerRef = useRef(null);

  // ── E-S1: resizable Effort grid columns ───────────────────────────────
  // NF-S2 REV 1.4 — read-only uses a SEPARATE default set + SEPARATE
  // localStorage key so consultant resizing never touches the PM layout.
  // PM defaults are untouched. Read-only narrows Predecessor/Status and
  // gives the freed width to the role hours/assignee column (column-level
  // widening happens at the <col> element).
  const DEFAULT_EFFORT_COLUMNS = useMemo(() => (readOnly ? {
    rowNum: 35,
    taskMode: 40,
    // REV 1.6 keeps Task Name (380) + the wider role <col> (150).
    name: 380,
    duration: 80,
    start: 110,
    finish: 110,
    predecessor: 40,
    // REV 1.6 — minimal 4px trim (44 → 40) eliminates the ~2px
    // horizontal overflow caused by the grid-wrap 1px-each-side border,
    // without shrinking the role / task-name columns.
    status: 40,
    totalHours: 80,
  } : {
    rowNum: 35,
    taskMode: 40,
    name: 380,
    duration: 80,
    start: 110,
    finish: 110,
    predecessor: 80,
    status: 70,
    totalHours: 80,
  }), [readOnly]);
  const MIN_EFFORT_COL_WIDTH = 30;
  // NF-S2 REV 1.6b — read-only key bumped to `.v2`. Returning users had
  // pre-REV-1.6 widths cached under `.columns.readonly` that overrode the
  // new narrower Status / wider role defaults. New key forces every
  // read-only mount to start from REV 1.6 defaults; we best-effort
  // delete the stale `.columns.readonly` value on read. The PM key is
  // untouched.
  const effortColumnsKey = readOnly
    ? `hrapp.effort.layout.${projectId}.columns.readonly.v2`
    : `hrapp.effort.layout.${projectId}.columns`;
  const [effortColWidths, setEffortColWidths] = useState(() => {
    try {
      if (readOnly) {
        try { localStorage.removeItem(`hrapp.effort.layout.${projectId}.columns.readonly`); } catch {}
      }
      const stored = localStorage.getItem(effortColumnsKey);
      if (stored) return { ...DEFAULT_EFFORT_COLUMNS, ...JSON.parse(stored) };
    } catch {}
    return DEFAULT_EFFORT_COLUMNS;
  });
  useEffect(() => {
    try {
      localStorage.setItem(effortColumnsKey, JSON.stringify(effortColWidths));
    } catch {}
  }, [effortColWidths, effortColumnsKey]);

  // SC-S1: Sell/Cost toggle — drives every monetary cell in the grid so the
  // per-role € cells and TOTAL reconcile horizontally. Persists per-project.
  const amountModeKey = `hrapp.effort.layout.${projectId}.amountMode`;
  const [amountMode, setAmountMode] = useState(() => {
    try {
      const stored = localStorage.getItem(amountModeKey);
      if (stored === 'sell' || stored === 'cost') return stored;
    } catch {}
    return 'sell';
  });
  useEffect(() => {
    try { localStorage.setItem(amountModeKey, amountMode); } catch {}
  }, [amountMode, amountModeKey]);

  const startEffortColResize = useCallback((e, colKey) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = effortColWidths[colKey];
    const onMove = (ev) => {
      const next = Math.max(MIN_EFFORT_COL_WIDTH, startW + (ev.clientX - startX));
      setEffortColWidths(w => ({ ...w, [colKey]: next }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [effortColWidths]);

  const showSnack = useCallback((msg, err = false) => {
    setSnack({ msg, err });
    if (snackTimerRef.current) clearTimeout(snackTimerRef.current);
    snackTimerRef.current = setTimeout(() => setSnack({ msg: '', err: false }), 2500);
  }, []);

  const handleStatusChange = (e) => {
    dispatch(updateProjectMetadata({ status: e.target.value }));
  };

  const handleExport = async () => {
    try {
      const blob = await projectGridService.exportProject(projectId);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project?.code || 'project'}-plan.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showSnack('Failed to export project', true);
    }
  };

  const handleExportExcel = async () => {
    try {
      const blob = await projectGridService.exportProjectExcel(projectId);
      const url = window.URL.createObjectURL(new Blob([blob], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project?.code || 'project'}-plan.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showSnack('Failed to export Excel', true);
    }
  };

  // Close popovers on outside click / Escape
  useEffect(() => {
    const close = () => { setCtx((c) => ({ ...c, open: false })); setModeDrop((m) => ({ ...m, open: false })); setStatusDrop((s) => ({ ...s, open: false })); };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('click', close); document.removeEventListener('keydown', onKey); };
  }, []);

  // Derived rows with summary flags and computed dates
  // NF-S4 — pass the holiday set so AUTO date cascades skip them too.
  const rows = useMemo(() => {
    const base = buildRows(tasks);
    return recalcDates(base, holidays);
  }, [tasks, holidays]);

  // PL-S4.5 — auto-derived project span (MIN start / MAX finish over
  // non-WBS rows). Read-only header display only; WBS is excluded so a
  // consultant-typed manual WBS date can never widen the project Period
  // (WBS-FIX-S1 isolation invariant). Recomputes live with `rows`, so
  // unsaved edits move the header before Save.
  const projectSpan = useMemo(() => {
    let start = null, finish = null;
    for (const r of rows) {
      if (r._start && (!start || r._start < start)) start = r._start;
      if (r._finish && (!finish || r._finish > finish)) finish = r._finish;
    }
    return { start, finish };
  }, [rows]);

  // Financial summary
  // WBS-FIX-S1 — WBS rows contribute nothing to project totals even if
  // they somehow carry stray hours; the explicit guard belt-and-suspenders
  // the planUtils-level fixes.
  // OFF-BUDGET-TASK-S1 — off-budget rows are EXCLUDED from SELL (no
  // revenue until a CR) but INCLUDED in COST (real spend the PM must
  // see). MARGIN naturally drops by the off-budget cost — that's the
  // exposure the flag is meant to surface.
  const fin = useMemo(() => {
    let hours = 0, sell = 0, cost = 0;
    for (const r of rows) {
      if (r.isSummaryDerived) continue;
      for (const role of roles) {
        const h = r.roles?.[role.id]?.plannedHours || 0;
        if (!h) continue;
        hours += h;
        if (!r.offBudget) sell += h * role.sellRatePerHour;
        cost += h * role.costRatePerHour;
      }
    }
    const margin = sell - cost;
    const marginPct = sell > 0 ? (margin / sell) * 100 : 0;
    return { hours, sell, cost, margin, marginPct };
  }, [rows, roles]);

  // OFF-BUDGET-TASK-S1 — header CR aggregate (computed live from rows so
  // it updates before Save). Hours = Σ plannedHours over off-budget
  // non-WBS non-summary rows. ClaimSell = Σ plannedHours × role.sell.
  // Off-budget COST is already in the main COST budget (fin.cost), so
  // we do NOT report it again here — the card is the recovery figure.
  const offBudgetSummary = useMemo(() => {
    let hours = 0, claimSell = 0;
    for (const r of rows) {
      if (r.isSummaryDerived || !r.offBudget) continue;
      for (const role of roles) {
        const h = r.roles?.[role.id]?.plannedHours || 0;
        if (!h) continue;
        hours += h;
        claimSell += h * role.sellRatePerHour;
      }
    }
    return { hours, claimSell };
  }, [rows, roles]);

  // TE-S1: the single-selection task, resolved by ID (null when 0 or >1 selected).
  const selTask = selectedIds.length === 1
    ? (tasks.find((t) => t.id === selectedIds[0]) || null)
    : null;

  // ── Handlers ────────────────────────────────────────────────────────
  // TE-S4: plain click = single select; Ctrl/Cmd = toggle; Shift = range.
  const onRowClick = (i, e) => {
    const id = rows[i].id;
    if (e && e.shiftKey && lastAnchorId != null) {
      const anchorIdx = rows.findIndex((r) => r.id === lastAnchorId);
      if (anchorIdx >= 0) {
        const [a, b] = anchorIdx < i ? [anchorIdx, i] : [i, anchorIdx];
        setSelectedIds(rows.slice(a, b + 1).map((r) => r.id));
        return;
      }
    }
    if (e && (e.ctrlKey || e.metaKey)) {
      setSelectedIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
      setLastAnchorId(id);
      return;
    }
    setSelectedIds([id]);
    setLastAnchorId(id);
  };
  const onRowContext = (e, i) => {
    // WBS-FIX-S2 — readOnly used to bail here. Now it opens a RESTRICTED
    // menu (Add WBS task / Task Details… / Delete WBS task only). PM-only
    // items (Insert/Indent/Outdent/Move/Delete/Mode) are gated below by
    // the `if (!readOnly)` blocks in the menu render.
    e.preventDefault(); e.stopPropagation();
    setSelectedIds([rows[i].id]);
    setCtx({ open: true, x: e.clientX, y: e.clientY });
  };

  const toggleCollapse = (taskId) => {
    setCollapsed((prev) => {
      const n = new Set(prev);
      if (n.has(taskId)) n.delete(taskId); else n.add(taskId);
      return n;
    });
  };
  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => {
    const s = new Set();
    for (const r of rows) if (r.isSummaryDerived) s.add(r.id);
    setCollapsed(s);
  };

  const openModeDrop = (e, taskId) => {
    if (readOnly) return;
    e.stopPropagation();
    setModeDrop({ open: true, x: e.clientX, y: e.clientY, taskId });
  };

  const openStatusDrop = (e, taskId) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setStatusDrop({ open: true, x: e.clientX, y: e.clientY, taskId });
  };
  const setTaskStatus = (taskId, status) => {
    setStatusDrop((s) => ({ ...s, open: false }));
    if (taskId == null) return;
    dispatch(updateTaskField(taskId, 'status', status));
  };

  // ── LF-S3 / LF-S4 / LF-S5: Save / Discard ─────────────────────────────
  // Returns true when the save succeeded (used by the nav-guard dialog).
  // ASGN-S3 §A.2 — the consultant Plan is view-only; the Save button
  // is gone from readOnly and Ctrl/Cmd+S becomes a no-op. The
  // `saveMyProjectWbs` branch below stays as defense (a tampered
  // client that reaches it hits 403 PLAN_READONLY from the server).
  const handleSave = useCallback(async () => {
    if (readOnly) return false;
    if (!dirty || saving) return false;
    setSaving(true);
    try {
      // LF-S5: a very large diff is split into sequential chunks; the common
      // case is a single chunk and behaves like one POST.
      // WBS-S3-FIX1 — pass wbsOnly=readOnly so the consultant payload skips
      // PE-S3 date injection (which would otherwise add recomputed
      // plannedStart/End to every costed AUTO task shifted by a WBS insert,
      // and save-wbs rejects each as ConsultantCannotEditCostedTask).
      const chunks = buildSaveChunks(state, holidays, readOnly);
      let version = chunks[0].version;
      const mergedIdMap = { tasks: [], roles: [] };
      const allErrors = [];
      // Resolve the save endpoint once: PM page vs consultant readOnly.
      let saveFn;
      if (readOnly) {
        const { saveMyProjectWbs } = await import('../../api/myProjectsApi');
        saveFn = (id, body) => saveMyProjectWbs(id, body);
      } else {
        saveFn = (id, body) => projectGridService.saveProject(id, body);
      }
      for (const chunk of chunks) {
        const result = await saveFn(projectId, { ...chunk, version });
        version = result?.version ?? version;
        (result?.idMap?.tasks || []).forEach((m) => mergedIdMap.tasks.push(m));
        (result?.idMap?.roles || []).forEach((m) => mergedIdMap.roles.push(m));
        (result?.errors || []).forEach((e) => allErrors.push(e));
      }
      dispatch(applySaveResult(mergedIdMap, version));
      if (allErrors.length > 0) {
        showSnack(`Saved with ${allErrors.length} error${allErrors.length > 1 ? 's' : ''}. See console.`, true);
        console.error('Save errors:', allErrors);
      } else {
        showSnack('Saved.', false);
      }
      return true;
    } catch (err) {
      if (err?.response?.status === 409) {
        setConflict(true); // LF-S5: conflict dialog (Reload / Cancel)
      } else if (err?.response?.status === 400 && err?.response?.data?.errors) {
        // SP-FIX-S1: structured validation errors. Log each to console so PO /
        // dev triage sees exactly which operations failed. Snack stays generic
        // until SP-FIX-S2 introduces per-row error UI.
        const errors = err.response.data.errors;
        console.error('Save validation failed:', errors);
        errors.forEach((e) =>
          console.error(`[${e.scope}] ${e.code}: ${e.message}`, e));
        showSnack(
          `Save failed: ${errors.length} validation error${errors.length > 1 ? 's' : ''}. See console.`,
          true,
        );
      } else {
        showSnack('Save failed.', true);
        console.error(err);
      }
      return false;
    } finally {
      setSaving(false);
    }
  }, [dirty, saving, state, projectId, dispatch, showSnack, holidays, readOnly]);

  const handleDiscard = useCallback(async () => {
    if (!dirty) return;
    if (await confirm({ message: 'Discard all unsaved changes?', danger: true, confirmLabel: 'Discard' })) {
      dispatch(discard());
      setSelectedIds([]);
      setEdit(null);
    }
  }, [dirty, dispatch, confirm]);

  // ── LF-S4/LF-S5: route-leave guard ────────────────────────────────────
  // Browser tab close — native confirm while the session is dirty.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // LF-S5: useBlocker (via <NavigationBlocker>) intercepts ALL in-app
  // navigation while dirty — including the sidebar.
  const onBlocked = useCallback((blocker) => {
    blockerRef.current = blocker;
    setShowDialog(true);
  }, []);
  const proceedBlocked = () => {
    const b = blockerRef.current;
    blockerRef.current = null;
    if (b && b.proceed) b.proceed();
  };
  const resetBlocked = () => {
    const b = blockerRef.current;
    blockerRef.current = null;
    if (b && b.reset) b.reset();
  };

  const handleDialogSave = async () => {
    const ok = await handleSave();
    setShowDialog(false);
    if (ok) proceedBlocked();
    else resetBlocked();
  };
  const handleDialogDiscard = () => {
    dispatch(discard());
    setShowDialog(false);
    proceedBlocked();
  };
  const handleDialogCancel = () => {
    setShowDialog(false);
    resetBlocked();
  };

  // LF-S5: 409 conflict — reload discards local changes for fresh server state.
  const handleConflictReload = () => {
    setConflict(false);
    if (reload) reload();
  };
  const handleConflictCancel = () => setConflict(false);

  // Ctrl/Cmd+S triggers the explicit save. WBS-S3 allows it in readOnly
  // too — handleSave routes through save-wbs when readOnly is set.
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSave]);

  const setMode = (mode) => {
    const taskId = modeDrop.taskId;
    setModeDrop((m) => ({ ...m, open: false }));
    if (taskId == null) return;
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    const currentMode = task.taskMode || 'AUTO';
    if (currentMode === mode) return;

    if (mode === 'MANUAL' && currentMode === 'AUTO') {
      // DP-S5: snapshot the current computed dates so the task stays in place.
      const row = rows.find((r) => r.id === taskId);
      if (row && row._start && row._finish) {
        dispatch(updateTaskField(taskId, 'plannedStartDate', toLocalIsoDate(row._start)));
        dispatch(updateTaskField(taskId, 'plannedEndDate', toLocalIsoDate(row._finish)));
      }
    } else if (mode === 'AUTO' && currentMode === 'MANUAL') {
      // DP-S5: drop the manual end date — AUTO re-derives it from Start + Duration.
      dispatch(updateTaskField(taskId, 'plannedEndDate', null));
      // Keep plannedStartDate — soft anchor when the task has no predecessor.
    }
    dispatch(updateTaskField(taskId, 'taskMode', mode));
  };

  // NF-S5 — open the Task Details drawer with the row's current values.
  const openTaskDetailsFor = useCallback((taskId) => {
    const t = tasks.find((x) => x.id === taskId);
    if (!t) return;
    setTaskDetails({
      taskId,
      name: t.name || '',
      taskMode: t.taskMode || 'AUTO',
      description: t.description || '',
      notes: t.notes || '',
      // Snapshot of the originals so Cancel can confirm "nothing changed"
      // and Apply can detect what to PATCH (WBS-FIX-S2 adds origName).
      origName: t.name || '',
      origDescription: t.description || '',
      origNotes: t.notes || '',
    });
  }, [tasks]);

  // NF-S5 — apply Task Details drawer edits to the session (batch Save
  // persists). WBS-S3 — readOnly now ALSO accumulates into the edit
  // session via updateTaskField; the PATCH endpoint is no longer hit
  // per-action.
  // WBS-S3-FIX1 — Description is now editable for WBS rows in readOnly
  // too (it's the consultant's own content); costed tasks still treat
  // Description as read-only (the textarea itself is disabled).
  // ASGN-S3 §A.2 — readOnly branch removed; Apply button is hidden
  // in readOnly and this handler is only reachable from PM mode.
  const applyTaskDetails = useCallback(async () => {
    if (!taskDetails) return;
    if (readOnly) { setTaskDetails(null); return; }
    if (taskDetails.description !== taskDetails.origDescription) {
      dispatch(updateTaskField(taskDetails.taskId, 'description', taskDetails.description));
    }
    if (taskDetails.notes !== taskDetails.origNotes) {
      dispatch(updateTaskField(taskDetails.taskId, 'notes', taskDetails.notes));
    }
    setTaskDetails(null);
  }, [taskDetails, readOnly, dispatch]);

  // WBS-RETIRE — the toggleWbsDone Done-checkbox helper is gone
  // with the WBS mode + its readOnly-only cell.

  const startEdit = (taskId, field, initial) => {
    // ASGN-S3 §A.2 — read-only sessions never edit cells (the WBS-S4
    // narrow exception for WBS duration / start / finish is retired
    // with the rest of the consultant edit surface).
    if (readOnly) return;
    // E-S5: idempotent — a click inside the already-open editor (e.g. the Name
    // cell keeps its onClick while editing) must not reset the typed value.
    setEdit((cur) => {
      if (cur && cur.taskId === taskId && cur.field === field) return cur;
      return { taskId, field, value: initial ?? '' };
    });
  };
  const cancelEdit = () => setEdit(null);

  // LF-S3: inline cell edits dispatch to the session — no HTTP request.
  const commitEdit = () => {
    if (!edit) return;
    const { taskId, field, value } = edit;
    const task = tasks.find((t) => t.id === taskId);
    if (!task) { setEdit(null); return; }

    if (field === 'name') {
      dispatch(updateTaskField(taskId, 'name', value));
    } else if (field === 'duration') {
      const dParsed = value === '' ? 0 : parseFloat(value);
      if (isNaN(dParsed) || dParsed < 0) { showSnack('Invalid duration', true); setEdit(null); return; }
      dispatch(updateTaskField(taskId, 'durationDays', dParsed));
      // WBS-RETIRE — the WBS-specific manual date math is gone with
      // the mode; every task falls through to the AUTO/MANUAL paths.
      // DP-S2: a MANUAL task's Finish is derived — recompute it from Start + Duration.
      const isManual = (task.taskMode || 'AUTO') === 'MANUAL';
      if (isManual && task.plannedStartDate) {
        const start = parseDateLocal(task.plannedStartDate);
        const newFinish = addBD(start, dParsed - 1, holidays);
        dispatch(updateTaskField(taskId, 'plannedEndDate', toLocalIsoDate(newFinish)));
      }
    } else if (field === 'predecessor') {
      // PRED-DISPLAY-BY-ROW-S1 — the typed value is now a ROW NUMBER
      // (the '#' ordinal, 1..rows.length), not a raw SortOrder. Translate
      // to the target row's `sortOrder` before storing. Storage semantics
      // (predecessorSortOrder = SortOrder) are unchanged; the scheduling
      // engine still resolves rows.find(r => r.sortOrder === predSort).
      // Empty / '0' clears the predecessor; self-reference is rejected.
      const raw = String(value).trim();
      if (raw === '' || raw === '0') {
        dispatch(updateTaskField(taskId, 'predecessorSortOrder', null));
        setEdit(null);
        return;
      }
      const n = parseInt(raw, 10);
      if (isNaN(n) || n < 1 || n > rows.length) {
        showSnack('Invalid predecessor row', true);
        setEdit(null);
        return;
      }
      const target = rows[n - 1];
      if (!target || target.id === taskId) {
        showSnack('A task cannot depend on itself', true);
        setEdit(null);
        return;
      }
      dispatch(updateTaskField(taskId, 'predecessorSortOrder', target.sortOrder));
    } else if (field === 'start') {
      // WBS-RETIRE — the WBS branch that handled MANUAL date math
      // is gone; every row falls through to AUTO/MANUAL.
      // PL-S1: date-driven. AUTO Start is computed — show a hint, do not save.
      const isAuto = (task.taskMode || 'AUTO') === 'AUTO';
      if (isAuto) {
        showSnack('Start date is computed in AUTO mode. Switch task to MANUAL to set Start explicitly.', false);
        setEdit(null);
        return;
      }
      let d = parseDmy(value);
      if (!d) { showSnack('Invalid date (use DD/MM/YY)', true); setEdit(null); return; }
      // DP-S1: weekend / holiday Start input → next business day.
      const original = new Date(d);
      d = normalizeToBusinessDay(d, 'forward', holidays);
      if (d.getTime() !== original.getTime()) {
        showSnack(`Start adjusted to next business day (${fmtD(d)})`, false);
      }
      // DP-S2: MANUAL Start keeps Duration constant — recompute Finish.
      // No Start-before-Finish guard: Finish is recomputed to follow the new
      // Start, so moving Start past the old Finish is valid (PO-approved).
      const dur = task.durationDays || 1;
      const newFinish = addBD(d, dur - 1, holidays);
      dispatch(updateTaskField(taskId, 'plannedStartDate', toLocalIsoDate(d)));
      dispatch(updateTaskField(taskId, 'plannedEndDate', toLocalIsoDate(newFinish)));
    } else if (field === 'finish') {
      // WBS-RETIRE — the WBS branch is gone; every row uses the
      // AUTO/MANUAL path below.
      // PL-S1: date-driven. AUTO Finish → recompute Duration; MANUAL → save end date.
      let d = parseDmy(value);
      if (!d) { showSnack('Invalid date (use DD/MM/YY)', true); setEdit(null); return; }
      // DP-S1: weekend / holiday Finish input → previous business day.
      const original = new Date(d);
      d = normalizeToBusinessDay(d, 'backward', holidays);
      if (d.getTime() !== original.getTime()) {
        showSnack(`Finish adjusted to previous business day (${fmtD(d)})`, false);
      }
      const frow = rows.find((r) => r.id === taskId);
      const isAuto = (task.taskMode || 'AUTO') === 'AUTO';
      // Predecessor check first — for an AUTO task whose Start is computed from
      // a predecessor, this gives the more informative message.
      if (isAuto && task.predecessorSortOrder) {
        const pred = rows.find((r) => r.sortOrder === task.predecessorSortOrder);
        if (pred && pred._finish && d <= pred._finish) {
          showSnack(`Finish must be after predecessor finish (${fmtD(pred._finish)})`, true);
          setEdit(null);
          return;
        }
      }
      // DP-S6: Finish == Start is allowed (a 1-day task); only a strictly
      // earlier Finish is rejected.
      if (frow && frow._start && d < frow._start) {
        showSnack(`Finish must be after Start (${fmtD(frow._start)})`, true);
        setEdit(null);
        return;
      }
      if (isAuto) {
        const newDuration = bdBetween(frow._start, d, holidays) + 1;
        dispatch(updateTaskField(taskId, 'durationDays', newDuration));
      } else {
        // DP-S2: MANUAL Finish — persist end date AND recompute Duration (inclusive).
        const newDuration = bdBetween(frow._start, d, holidays) + 1;
        dispatch(updateTaskField(taskId, 'plannedEndDate', toLocalIsoDate(d)));
        dispatch(updateTaskField(taskId, 'durationDays', newDuration));
      }
    } else if (field.startsWith('role_')) {
      const roleId = parseInt(field.slice(5), 10);
      const h = value === '' ? 0 : parseFloat(value);
      if (isNaN(h) || h < 0) { showSnack('Hours must be 0 or more', true); setEdit(null); return; }
      const util = task.durationDays > 0
        ? Math.round((h / (task.durationDays * 8)) * 10000) / 100
        : 0;
      dispatch(updateTaskField(taskId, 'roles', {
        ...(task.roles || {}), [roleId]: { plannedHours: h, utilizationPercent: util },
      }));
    }
    setEdit(null);
  };

  const maxSortOrder = () =>
    (tasks.length > 0 ? Math.max(...tasks.map((t) => t.sortOrder || 0)) : 0);

  // NF-S4 — find the same-level sibling immediately before / after the
  // slot a new task `outlineLevel` is being inserted at index `pos` in
  // `rows`. "Same level" means equal outlineLevel; the scan stops when
  // outlineLevel drops below (we've left the current parent's subtree).
  const findSameLevelSiblings = (pos, outlineLevel) => {
    let prev = null;
    for (let j = pos - 1; j >= 0; j--) {
      const r = rows[j];
      if (r.outlineLevel < outlineLevel) break;
      if (r.outlineLevel === outlineLevel) { prev = r; break; }
    }
    let next = null;
    for (let j = pos; j < rows.length; j++) {
      const r = rows[j];
      if (r.outlineLevel < outlineLevel) break;
      if (r.outlineLevel === outlineLevel) { next = r; break; }
    }
    return { prev, next };
  };

  const insertTask = (where) => {
    setCtx((c) => ({ ...c, open: false }));
    const refIdx = selectedIds.length === 1
      ? rows.findIndex((r) => r.id === selectedIds[0])
      : rows.length - 1;
    const ref = rows[refIdx];
    const outlineLevel = ref ? ref.outlineLevel : 0;
    const ids = rows.map((r) => r.id);
    const pos = where === 'above' ? refIdx : refIdx + 1;

    // NF-S4 (REV 1.1) — inherit the reference task's mode so the chain
    // semantics propagate; default to AUTO when no reference exists.
    const inheritedMode = ref?.taskMode || 'AUTO';
    const isAuto = inheritedMode === 'AUTO';

    // NF-S4 (REV 1.1) — same-level siblings around the new slot drive
    // the AUTO dependency rewire. `prev` is also the natural predecessor
    // for a brand-new AUTO task.
    const { prev: prevSibling, next: nextSibling } =
      findSameLevelSiblings(pos < 0 ? rows.length : pos, outlineLevel);
    const initialPredecessor = (isAuto && prevSibling)
      ? prevSibling.sortOrder
      : null;

    // The reducer will assign this exact temp id to the new task.
    const tempId = state.tempIdCounter;
    dispatch(addTask({
      name: 'New Task', outlineLevel, durationDays: 1,
      taskMode: inheritedMode, status: 'NotStarted',
      sortOrder: maxSortOrder() + 1,
      predecessorSortOrder: initialPredecessor,
    }));
    ids.splice(pos < 0 ? ids.length : pos, 0, tempId);
    // SAVECOUNT-S1 — the reorder here is a side-effect of the insert
    // above; the add is the one user action, the reorder must not
    // double-count into dirtyCount.
    dispatch(reorderTasks(ids, { sideEffect: true }));

    // NF-S4 (REV 1.1) — if the next same-level sibling used to depend
    // on `prevSibling`, repoint it at the new task so the chain becomes
    // prev → new → next. After reorderTasks the new task's sortOrder is
    // exactly `insertPos + 1` (its index in `ids` after the splice).
    if (isAuto && prevSibling && nextSibling
      && nextSibling.predecessorSortOrder === prevSibling.sortOrder) {
      const insertPos = pos < 0 ? ids.length - 1 : pos;
      const newSortForN = insertPos + 1;
      dispatch(updateTaskField(nextSibling.id, 'predecessorSortOrder', newSortForN));
    }
  };

  // WBS-S3 — readOnly (consultant) "Add WBS task". Now routes through
  // ASGN-S3 §A.2 — the readOnly variants of Add-WBS / Delete-WBS
  // and the `pendingOpenTaskId` post-remount hook are removed. The
  // consultant Plan is view-only and no longer accumulates task
  // mutations into the edit session (`SaveMyProjectWbs` would 403
  // PLAN_READONLY on any of them). PM paths keep their own
  // `addChildTask` / `deleteSelected` unaffected.

  // WBS-FIX-S1 — context-menu "Add WBS task" (was "Add Child"). The new
  // task is ALWAYS WBS, regardless of the parent's mode. Appended after
  // the selected task's last descendant. Single-select only — like Move
  // WBS-RETIRE — the `addChildTask` handler produced WBS-mode rows
  // exclusively; retired with the mode. The context-menu "Add WBS
  // task" entry that called it is likewise removed below.

  const appendTask = () => {
    dispatch(addTask({
      name: 'New Task', outlineLevel: 0, durationDays: 1,
      taskMode: 'AUTO', status: 'NotStarted', sortOrder: maxSortOrder() + 1,
    }));
  };

  // E-S5: ghost row — adds a task to the session, then focuses its Name cell.
  const handleGhostRowClick = () => {
    const tempId = state.tempIdCounter;
    dispatch(addTask({
      name: '', outlineLevel: 0, durationDays: 1,
      taskMode: 'AUTO', status: 'NotStarted', sortOrder: maxSortOrder() + 1,
    }));
    setSelectedIds([tempId]);
    setEdit({ taskId: tempId, field: 'name', value: '' });
  };

  // TE-S4: delete every selected task — the reducer cascades children.
  const deleteSelected = async () => {
    setCtx((c) => ({ ...c, open: false }));
    if (selectedIds.length === 0) return;
    let msg;
    if (selectedIds.length === 1 && selTask) {
      const idx = rows.findIndex((r) => r.id === selTask.id);
      const kidIdx = descendants(rows, idx);
      msg = kidIdx.length > 0
        ? `Delete "${selTask.name}" and ${kidIdx.length} child task(s)?`
        : `Delete "${selTask.name}"?`;
    } else {
      msg = `Delete ${selectedIds.length} selected tasks?`;
    }
    if (!(await confirm({ message: msg, danger: true, confirmLabel: 'Delete' }))) return;
    for (const id of selectedIds) dispatch(deleteTask(id));
    setSelectedIds([]);
  };

  // TE-S4: indent every selected row, in row order; skip rows that can't indent.
  const doIndent = () => {
    setCtx((c) => ({ ...c, open: false }));
    const idsInOrder = rows.filter((r) => selectedIds.includes(r.id)).map((r) => r.id);
    for (const id of idsInOrder) {
      const idx = rows.findIndex((r) => r.id === id);
      if (idx <= 0) continue;
      const row = rows[idx];
      const prev = rows[idx - 1];
      if (prev.outlineLevel < row.outlineLevel) continue; // can't indent beyond parent
      dispatch(updateTaskField(id, 'outlineLevel', row.outlineLevel + 1));
    }
  };

  // TE-S4: outdent every selected row; skip rows already at outline level 0.
  const doOutdent = () => {
    setCtx((c) => ({ ...c, open: false }));
    const idsInOrder = rows.filter((r) => selectedIds.includes(r.id)).map((r) => r.id);
    for (const id of idsInOrder) {
      const row = rows.find((r) => r.id === id);
      if (!row || row.outlineLevel === 0) continue;
      dispatch(updateTaskField(id, 'outlineLevel', row.outlineLevel - 1));
    }
  };

  const doMove = (delta) => {
    if (!selTask) return;
    const idx = rows.findIndex((r) => r.id === selTask.id);
    if (idx < 0) return;
    const row = rows[idx];
    const blockStart = idx;
    let blockEnd = idx;
    if (row.isSummaryDerived) {
      const descs = descendants(rows, idx);
      if (descs.length > 0) blockEnd = descs[descs.length - 1];
    }
    const len = blockEnd - blockStart + 1;
    const ids = rows.map((r) => r.id);
    if (delta === -1) {
      if (blockStart === 0) return;
      const block = ids.splice(blockStart, len);
      ids.splice(blockStart - 1, 0, ...block);
      dispatch(reorderTasks(ids));
    } else {
      if (blockEnd >= rows.length - 1) return;
      const block = ids.splice(blockStart, len);
      ids.splice(blockStart + 1, 0, ...block);
      dispatch(reorderTasks(ids));
    }
    // TE-S1: selection is by task ID — it follows the moved task automatically.
  };

  // TE-S3: toolbar / context-menu ops first commit any pending inline edit,
  // so a typed-but-uncommitted value is never lost (Bug #1).
  const withCommit = (fn) => (...args) => {
    if (edit) commitEdit();
    return fn(...args);
  };
  const doIndentClick = withCommit(doIndent);
  const doOutdentClick = withCommit(doOutdent);
  const doMoveClick = (delta) => withCommit(() => doMove(delta))();
  const deleteSelectedClick = withCommit(deleteSelected);
  const appendTaskClick = withCommit(appendTask);
  const insertTaskAboveClick = withCommit(() => insertTask('above'));
  const insertTaskBelowClick = withCommit(() => insertTask('below'));
  // WBS-RETIRE — `addChildClick` produced WBS-mode rows; the helper
  // and the context-menu entry that called it are both gone.

  // ── Role dialog (dual-panel picker) ──────────────────────────────────
  const openRoleDialog = () => {
    const inProject = new Set(roles.map((r) => r.abbreviation.toUpperCase()));
    const avail = ROLE_CATALOG
      .filter((c) => !inProject.has(c.abbr.toUpperCase()))
      .map((c) => ({ abbr: c.abbr, name: c.name, sell: c.sell, cost: c.cost, isCustom: false }));
    const proj = roles.map((r) => ({
      id: r.id, abbr: r.abbreviation, name: r.roleName,
      sell: r.sellRatePerHour, cost: r.costRatePerHour,
      isCustom: !ROLE_CATALOG.some((c) => c.abbr.toUpperCase() === r.abbreviation.toUpperCase()),
    }));
    setRdAvailable(avail);
    setRdProject(proj);
    setRdSelLeft(-1);
    setRdSelRight(-1);
    setRdView('panels');
    setRdForm({ abbr: '', name: '', sell: '', cost: '', err: '' });
    setRdEditMode(null);
    setRoleDialog({ open: true });
  };

  const closeRoleDialog = () => setRoleDialog({ open: false });

  const rdMoveRight = () => {
    if (rdSelLeft < 0 || rdSelLeft >= rdAvailable.length) return;
    const item = rdAvailable[rdSelLeft];
    setRdAvailable((prev) => prev.filter((_, i) => i !== rdSelLeft));
    setRdProject((prev) => [...prev, item]);
    setRdSelLeft(-1);
  };
  const rdMoveAllRight = () => {
    if (rdAvailable.length === 0) return;
    setRdProject((prev) => [...prev, ...rdAvailable]);
    setRdAvailable([]);
    setRdSelLeft(-1);
  };
  const rdMoveLeft = () => {
    if (rdSelRight < 0 || rdSelRight >= rdProject.length) return;
    const item = rdProject[rdSelRight];
    setRdProject((prev) => prev.filter((_, i) => i !== rdSelRight));
    setRdAvailable((prev) => [...prev, item]);
    setRdSelRight(-1);
  };
  const rdMoveAllLeft = () => {
    if (rdProject.length === 0) return;
    setRdAvailable((prev) => [...prev, ...rdProject]);
    setRdProject([]);
    setRdSelRight(-1);
  };

  const rdOpenNewForm = () => {
    setRdEditMode(null);
    setRdForm({ abbr: '', name: '', sell: '', cost: '', err: '' });
    setRdView('form');
  };
  const rdOpenEditForm = () => {
    let side, idx, r;
    if (rdSelRight >= 0 && rdSelRight < rdProject.length) {
      side = 'right'; idx = rdSelRight; r = rdProject[idx];
    } else if (rdSelLeft >= 0 && rdSelLeft < rdAvailable.length) {
      side = 'left'; idx = rdSelLeft; r = rdAvailable[idx];
    } else return;
    setRdEditMode({ side, idx });
    setRdForm({ abbr: r.abbr, name: r.name, sell: String(r.sell), cost: String(r.cost), err: '' });
    setRdView('form');
  };
  const rdBackToPanels = () => {
    setRdEditMode(null);
    setRdForm((f) => ({ ...f, err: '' }));
    setRdView('panels');
  };

  const rdSubmitForm = async () => {
    const abbr = rdForm.abbr.trim().toUpperCase();
    const name = rdForm.name.trim();
    const sell = parseFloat(rdForm.sell);
    const cost = parseFloat(rdForm.cost);
    const isEdit = rdEditMode !== null;
    if (!isEdit) {
      if (!abbr || !/^[A-Z]{1,4}$/.test(abbr)) {
        setRdForm((f) => ({ ...f, err: 'Abbreviation required: 1–4 letters (e.g. DBA)' })); return;
      }
      const dupAvail = rdAvailable.some((r) => r.abbr.toUpperCase() === abbr);
      const dupProj = rdProject.some((r) => r.abbr.toUpperCase() === abbr);
      if (dupAvail || dupProj) {
        setRdForm((f) => ({ ...f, err: `Role "${abbr}" already exists` })); return;
      }
    }
    if (!name) { setRdForm((f) => ({ ...f, err: 'Role name is required' })); return; }
    if (name.length > 100) { setRdForm((f) => ({ ...f, err: 'Role name must be 100 characters max' })); return; }
    if (isNaN(sell) || sell < 0) { setRdForm((f) => ({ ...f, err: 'Sell rate must be a non-negative number' })); return; }
    if (isNaN(cost) || cost < 0) { setRdForm((f) => ({ ...f, err: 'Cost rate must be a non-negative number' })); return; }
    if (cost > sell && !(await confirm({ message: 'Cost rate exceeds sell rate. Save anyway?' }))) return;

    if (!isEdit) {
      const newRole = { abbr, name, sell, cost, isCustom: true };
      setRdAvailable((prev) => [...prev, newRole]);
    } else {
      const { side, idx } = rdEditMode;
      const updater = (list) => list.map((r, i) => i === idx ? { ...r, name, sell, cost } : r);
      if (side === 'right') setRdProject(updater);
      else setRdAvailable(updater);
    }
    rdBackToPanels();
  };

  // LF-S3: apply the dual-panel result to the session (no HTTP request).
  const rdSaveChanges = () => {
    const currentById = new Map(roles.map((r) => [r.id, r]));
    const desiredIds = new Set(rdProject.filter((r) => r.id != null).map((r) => r.id));
    // Removed roles
    for (const r of roles) {
      if (!desiredIds.has(r.id)) dispatch(deleteRole(r.id));
    }
    // Created + updated roles
    for (const r of rdProject) {
      if (r.id == null) {
        dispatch(addRole({
          abbreviation: r.abbr, roleName: r.name,
          sellRatePerHour: r.sell, costRatePerHour: r.cost,
        }));
      } else {
        const cur = currentById.get(r.id);
        if (!cur) continue;
        if (cur.abbreviation !== r.abbr) dispatch(updateRoleField(r.id, 'abbreviation', r.abbr));
        if (cur.roleName !== r.name) dispatch(updateRoleField(r.id, 'roleName', r.name));
        if (cur.sellRatePerHour !== r.sell) dispatch(updateRoleField(r.id, 'sellRatePerHour', r.sell));
        if (cur.costRatePerHour !== r.cost) dispatch(updateRoleField(r.id, 'costRatePerHour', r.cost));
      }
    }
    closeRoleDialog();
  };

  // Keyboard: Tab/Shift+Tab indent, Delete, only when no inline edit is active
  useEffect(() => {
    const onKey = (e) => {
      if (edit || roleDialog.open) return;
      if (e.key === 'Tab' && selectedIds.length > 0) { e.preventDefault(); if (e.shiftKey) doOutdent(); else doIndent(); }
      else if (e.key === 'Delete' && selectedIds.length > 0) { e.preventDefault(); deleteSelected(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, rows, edit, roleDialog.open]);

  // Keyboard navigation inside the role dialog
  useEffect(() => {
    if (!roleDialog.open) return;
    const onKey = (e) => {
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA')) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        if (rdView === 'form') rdBackToPanels(); else closeRoleDialog();
        return;
      }
      if (rdView !== 'panels') return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (rdSelLeft >= 0 && rdSelLeft < rdAvailable.length - 1) setRdSelLeft(rdSelLeft + 1);
        else if (rdSelRight >= 0 && rdSelRight < rdProject.length - 1) setRdSelRight(rdSelRight + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (rdSelLeft > 0) setRdSelLeft(rdSelLeft - 1);
        else if (rdSelRight > 0) setRdSelRight(rdSelRight - 1);
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (rdSelLeft >= 0) { e.preventDefault(); rdMoveRight(); }
      } else if (e.key === 'ArrowLeft' || e.key === 'Backspace') {
        if (rdSelRight >= 0) { e.preventDefault(); rdMoveLeft(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleDialog.open, rdView, rdSelLeft, rdSelRight, rdAvailable, rdProject]);

  if (!project) return null;

  return (
    <div className={`pdp${readOnly ? ' pdp-readonly' : ''}`} data-testid={readOnly ? 'project-detail-readonly' : undefined}>
      <div className="project-header">
        <span className="back" onClick={() => navigate(readOnly ? '/my-projects' : '/projects')}>
          {readOnly ? '← Back to My Projects' : '← Back to Projects'}
        </span>
        {/* QLOG-S2.1 §A.3 — Delivery ↔ Plan segmented toggle. Only in
            the readOnly (My Projects) view; PM /projects/:id keeps its
            existing header untouched. */}
        {readOnly && project?.id && <ProjectViewToggle projectId={project.id} active="plan" />}
        <h1>{project.name}</h1>
        <div className="meta">
          <span><strong>Code:</strong> {project.code}</span>
          {project.isBillable === false && (
            <span className="pdp-badge-nonbillable">Non-billable</span>
          )}
          {project.clientName && <span><strong>Client:</strong> {project.clientName}</span>}
          {/* PL-S4.5 — read-only project Period derived from the same `rows`
              that feed the grid. MIN start / MAX finish, WBS excluded. Updates
              live as the user edits task dates (before Save). Identical in PM
              and consultant readOnly views. No edit affordance. */}
          <span data-testid="pdp-project-period">
            <strong>Period:</strong>{' '}
            {projectSpan.start ? fmtD(projectSpan.start) : '—'} – {projectSpan.finish ? fmtD(projectSpan.finish) : '—'}
          </span>
          {readOnly ? (
            <>
              <span
                className="status-display"
                style={{
                  backgroundColor: STATUS_COLORS[project?.status]?.bg || '#e0e0e0',
                  color: STATUS_COLORS[project?.status]?.color || '#555',
                  padding: '2px 8px',
                  borderRadius: 4,
                }}
              >
                {project?.status === 'OnHold' ? 'On Hold' : (project?.status || 'Draft')}
              </span>
              {/* WI-S4 §1 — the S1 "Delivery (preview)" link is
                  removed: /my-projects/:id IS DeliveryPage now, and
                  this screen is the /plan sub-route reached via the
                  DeliveryPage's own "Plan (read-only)" link. */}
            </>
          ) : (
            <>
              <select
                className="status-select"
                value={project?.status || 'Draft'}
                onChange={handleStatusChange}
                style={{
                  backgroundColor: STATUS_COLORS[project?.status]?.bg || '#e0e0e0',
                  color: STATUS_COLORS[project?.status]?.color || '#555',
                }}
              >
                <option value="Draft">Draft</option>
                <option value="Active">Active</option>
                <option value="OnHold">On Hold</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
              <button
                className="pdp-edit-btn"
                data-testid="pdp-edit-btn"
                onClick={() => setEditOpen(true)}
                title="Edit project properties"
              >
                &#x270E;
              </button>
              <span>Currency: {project.currencyCode}</span>
            </>
          )}
        </div>
      </div>

      {!readOnly && (
        <ProjectPropertiesDrawer
          mode="edit"
          project={project}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSuccess={() => { setEditOpen(false); if (reload) reload(); }}
        />
      )}

      {!readOnly && (
        <div className="fin-cards">
          <div className="fin-card"><div className="lbl">Total Hours</div><div className="val">{fin.hours.toFixed(0)}</div></div>
          <div className="fin-card"><div className="lbl">Sell Budget</div><div className="val">{fmtC(fin.sell)}</div></div>
          <div className="fin-card cost"><div className="lbl">Cost Budget</div><div className="val">{fmtC(fin.cost)}</div></div>
          <div className="fin-card margin"><div className="lbl">Margin</div><div className="val">{fmtC(fin.margin)} ({fin.marginPct.toFixed(0)}%)</div></div>
          {/* OFF-BUDGET-TASK-S1 — show the CR aggregate only when there
              are off-budget hours. Hours + claimable Sell at role rate.
              Cost is NOT shown here — it's already in Cost Budget. */}
          {offBudgetSummary.hours > 0 && (
            <div className="fin-card offbudget" data-testid="fin-card-offbudget">
              <div className="lbl">OFF-BUDGET (за CR)</div>
              <div className="val">{offBudgetSummary.hours.toFixed(0)} h · {fmtC(offBudgetSummary.claimSell)} claimable</div>
            </div>
          )}
        </div>
      )}

      <div className="toolbar">
        <div className="view-toggle">
          <button
            className={view === 'effort' ? 'active' : ''}
            onClick={() => handleViewChange('effort')}
            type="button"
          >
            Effort Estimation
          </button>
          <button
            className={view === 'gantt' ? 'active' : ''}
            onClick={() => handleViewChange('gantt')}
            type="button"
          >
            Gantt
          </button>
        </div>
        {!readOnly && (
          <div className="amount-toggle">
            <button
              className={amountMode === 'sell' ? 'active' : ''}
              onClick={() => setAmountMode('sell')}
              title="Show sell amounts (gross revenue)"
              type="button"
            >Sell €</button>
            <button
              className={amountMode === 'cost' ? 'active' : ''}
              onClick={() => setAmountMode('cost')}
              title="Show cost amounts (internal expense)"
              type="button"
            >Cost €</button>
          </div>
        )}
        {!readOnly && (
          <>
            <div className="sep"></div>
            <span className="lbl">HIERARCHY:</span>
            <button title="Indent (Tab)" onClick={doIndentClick} disabled={selectedIds.length === 0}>→ Indent</button>
            <button title="Outdent (Shift+Tab)" onClick={doOutdentClick} disabled={selectedIds.length === 0}>← Outdent</button>
            <div className="sep"></div>
            <span className="lbl">MOVE:</span>
            <button onClick={() => doMoveClick(-1)} disabled={selectedIds.length !== 1}>↑ Up</button>
            <button onClick={() => doMoveClick(1)} disabled={selectedIds.length !== 1}>↓ Down</button>
            <div className="sep"></div>
            <button onClick={deleteSelectedClick} disabled={selectedIds.length === 0}>✕ Delete</button>
            <div className="sep"></div>
            <button className="accent" onClick={appendTaskClick}>+ Add Task</button>
          </>
        )}
        <div className="sep"></div>
        <button onClick={expandAll}>Expand All</button>
        <button onClick={collapseAll}>Collapse All</button>
        {!readOnly && (
          <>
            <div className="sep"></div>
            <button className="accent" onClick={openRoleDialog}>+ Add Role</button>
            <div className="sep"></div>
            <button className="accent" onClick={handleExport} title="Download project plan as JSON">&#x1F4E5; Download Plan</button>
            <button className="accent" onClick={handleExportExcel} title="Download project plan as Excel">&#x1F4CA; Download Excel</button>
            {/* RA-S5 — "Manage Assignments" + "Bulk Assign Consultants"
                retired with the whole personal-assignment layer. */}
            {hasPermission?.('ManageProjects') && (
              <button
                className="accent"
                onClick={() => setManageTeamOpen(true)}
                title="Manage the project's consultant team"
                data-testid="manage-team-btn"
              >
                👥 Manage Team
              </button>
            )}
            <button
              ref={prefsAnchorRef}
              className="accent"
              onClick={() => setPrefsOpen((o) => !o)}
              title="Effort grid view preferences"
              data-testid="effort-prefs-btn"
            >
              ⚙
            </button>
            <div className="lf-save-group" data-testid="lf-save-group">
              <button className="btn btn-primary" data-testid="lf-save-btn"
                disabled={!dirty || saving} onClick={handleSave}>
                {saving ? 'Saving…' : `Save${dirty ? ` (${dCount})` : ''}`}
              </button>
              <button className="btn" data-testid="lf-discard-btn"
                disabled={!dirty || saving} onClick={handleDiscard}>
                Discard
              </button>
              <div className="sr-only" aria-live="polite" data-testid="lf-save-status">
                {saving ? 'Saving changes' : snack.msg}
              </div>
            </div>
            <span className="hint">Click a cell to edit • Right-click row for menu</span>
          </>
        )}

        {/* ASGN-S3 §A.2 — the consultant Save/Discard bar is
            removed together with the Add/Delete WBS affordances.
            The Plan is view-only; there's nothing to save. */}
      </div>

      {view === 'gantt' ? (
        <GanttView
          project={project}
          roles={roles}
          tasks={tasks}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll}
          holidays={holidays}
        />
      ) : (
      <div
        className="grid-wrap"
        data-testid="grid-wrap"
        // GRID-HSCROLL-FIX-S1 — the readOnly `overflow-x: hidden` inline
        // override (NF-S2 REV 1.6c) was suppressing the phantom 2px bar
        // at rest, but it ALSO clipped the right columns the moment a
        // user widened the Name column past the viewport. The phantom
        // 2px is now killed at the stylesheet level by box-sizing:
        // border-box on .pdp .grid-wrap, so the inline override is no
        // longer needed and both modes use stylesheet overflow-x: auto.
      >
        <table style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: effortColWidths.rowNum }} />
            <col style={{ width: effortColWidths.taskMode }} />
            <col style={{ width: effortColWidths.name }} />
            <col style={{ width: effortColWidths.duration }} />
            <col style={{ width: effortColWidths.start }} />
            <col style={{ width: effortColWidths.finish }} />
            <col style={{ width: effortColWidths.predecessor }} />
            <col style={{ width: effortColWidths.status }} />
            {roles.map((r) => (
              <React.Fragment key={`cg-${r.id}`}>
                {/* REV 1.4 — widen the role hours column in read-only so
                    one-line "К. Дончев (16h)" fits without ballooning row
                    height. PM column stays 60px. */}
                <col style={{ width: readOnly ? 150 : 60 }} />
                {!readOnly && <col style={{ width: 90 }} />}
              </React.Fragment>
            ))}
            <col style={{ width: effortColWidths.totalHours }} />
            {!readOnly && <col style={{ width: 110 }} />}
          </colgroup>
          <thead>
            <tr>
              <th rowSpan={2}>#<span className="col-resizer" onMouseDown={(e) => startEffortColResize(e, 'rowNum')} data-testid="effort-col-resizer-rowNum" /></th>
              <th rowSpan={2}>Mode<span className="col-resizer" onMouseDown={(e) => startEffortColResize(e, 'taskMode')} data-testid="effort-col-resizer-taskMode" /></th>
              <th rowSpan={2}>Task Name<span className="col-resizer" onMouseDown={(e) => startEffortColResize(e, 'name')} data-testid="effort-col-resizer-name" /></th>
              <th rowSpan={2}>Duration<span className="col-resizer" onMouseDown={(e) => startEffortColResize(e, 'duration')} data-testid="effort-col-resizer-duration" /></th>
              <th rowSpan={2}>Start<span className="col-resizer" onMouseDown={(e) => startEffortColResize(e, 'start')} data-testid="effort-col-resizer-start" /></th>
              <th rowSpan={2}>Finish<span className="col-resizer" onMouseDown={(e) => startEffortColResize(e, 'finish')} data-testid="effort-col-resizer-finish" /></th>
              <th rowSpan={2}>Pred.<span className="col-resizer" onMouseDown={(e) => startEffortColResize(e, 'predecessor')} data-testid="effort-col-resizer-predecessor" /></th>
              <th rowSpan={2}>Status<span className="col-resizer" onMouseDown={(e) => startEffortColResize(e, 'status')} data-testid="effort-col-resizer-status" /></th>
              {roles.map((r) => (
                <th
                  key={r.id}
                  className="rh"
                  colSpan={readOnly ? 1 : 2}
                  rowSpan={readOnly ? 2 : 1}
                  title={readOnly ? r.roleName : `${r.roleName} — Sell: ${r.sellRatePerHour}€/h, Cost: ${r.costRatePerHour}€/h`}
                >
                  {r.abbreviation}
                </th>
              ))}
              <th className="th-total" rowSpan={2}>TOTAL h<span className="col-resizer" onMouseDown={(e) => startEffortColResize(e, 'totalHours')} data-testid="effort-col-resizer-totalHours" /></th>
              {!readOnly && <th className="th-total" rowSpan={2}>TOTAL</th>}
            </tr>
            {!readOnly && (
              <tr className="sub">
                {roles.map((r) => (
                  <React.Fragment key={`sh-${r.id}`}>
                    <th>hours</th>
                    <th>{amountMode === 'sell' ? 'sell €' : 'cost €'}</th>
                  </React.Fragment>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((row, i) => {
              if (!isVisible(rows, i, collapsed)) return null;
              const sm = row.isSummaryDerived;
              const isSel = selectedIds.includes(row.id);
              return (
                <tr
                  key={row.id}
                  // NF-S5 — `task-wbs` class adds italic + 📋 + opacity 0.8
                  // in both PM and read-only views.
                  // WBS-FOCUS-FLAG-S1 — `wbs-flag-{focus|ok|problem}` paints
                  // the row with the PM-chosen colour. Only WBS rows can
                  // carry a flag (costed rows never get the class).
                  // OFF-BUDGET-TASK-S1 — `task-offbudget` paints the row
                  // with the CR accent + adds a "CR" badge to the name.
                  // WBS-RETIRE — `task-wbs` and `wbs-flag-*` classes
                  // are dead; every row is AUTO or MANUAL.
                  className={`${sm ? `sum l${row.outlineLevel}` : ''} ${isSel ? 'sel' : ''}${row.offBudget ? ' task-offbudget' : ''}`}
                  data-testid={`pdp-task-row-${row.id}`}
                  onClick={(e) => onRowClick(i, e)}
                  onContextMenu={(e) => onRowContext(e, i)}
                >
                  <td className="rn">{i + 1}</td>
                  <td className="tm">
                    {(() => {
                      // WBS-RETIRE — mode badge is only A / M now.
                      const m = row.taskMode || 'AUTO';
                      const cls = m === 'AUTO' ? 'a' : 'm';
                      const letter = m === 'AUTO' ? 'A' : 'M';
                      const title = m === 'AUTO' ? 'Auto Scheduled' : 'Manually Scheduled';
                      return (
                        <span
                          className={`mi ${cls}`}
                          onClick={(e) => openModeDrop(e, row.id)}
                          title={title}
                        >{letter}</span>
                      );
                    })()}
                  </td>
                  {/* Task Name. WBS-RETIRE — the readOnly-WBS click
                      shortcut is gone with the mode. */}
                  <td className="tn" onClick={(e) => {
                    if (e.ctrlKey || e.metaKey || e.shiftKey) return;
                    startEdit(row.id, 'name', row.name);
                  }}>
                    <span className="ind" style={{ width: row.outlineLevel * 20 }}></span>
                    {sm ? (
                      <span className="eb" onClick={(e) => { e.stopPropagation(); toggleCollapse(row.id); }}>
                        {collapsed.has(row.id) ? '►' : '▼'}
                      </span>
                    ) : <span className="spacer" />}
                    {edit && edit.taskId === row.id && edit.field === 'name' ? (
                      <input autoFocus className="ie-input" style={{ width: `calc(100% - ${row.outlineLevel * 20 + 18}px)`, fontWeight: sm ? 700 : 400 }}
                        value={edit.value}
                        onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                        onBlur={commitEdit}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); else if (e.key === 'Escape') cancelEdit(); }} />
                    ) : (<span className="tt">{row.name}</span>)}
                    {/* OFF-BUDGET-TASK-S1 — small badge next to the task
                        name so the PM spots the off-budget rows at a
                        glance. Render unconditionally based on the flag
                        (both PM and consultant views). */}
                    {row.offBudget && (
                      <span className="cr-badge" data-testid={`pdp-cr-badge-${row.id}`} title="Off-budget (за CR)">CR</span>
                    )}
                  </td>

                  {/* WBS-S4 — WBS rows now render their own MANUAL
                      Duration/Start/Finish (consultant-edited; blank when
                      unset). Predecessor stays "—" — a WBS task is never a
                      predecessor (WbsCannotHaveChildren guards both
                      directions). These three cells are editable for WBS
                      rows even in readOnly; the inline edits dispatch
                      updateTaskField into the session and ride the WBS-S3
                      batch Save. NO cascade to other tasks. */}
                  {/* Duration — WBS-RETIRE: WBS branch removed. */}
                  {sm
                    ? <td className="du zero">{row._spanDays || 0}d</td>
                    : (edit && edit.taskId === row.id && edit.field === 'duration'
                        ? <td className="du"><input autoFocus className="ie-input center" value={edit.value}
                            onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                            onBlur={commitEdit}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); else if (e.key === 'Escape') cancelEdit(); }} /></td>
                        : <td className="du ed" onClick={(e) => { if (e.ctrlKey || e.metaKey || e.shiftKey) return; startEdit(row.id, 'duration', row.durationDays); }}>{row.durationDays}d</td>)
                  }
                  {/* Start — editable for all leaf tasks (PL-S1; AUTO commit shows a hint). WBS-RETIRE: WBS branch removed. */}
                  {sm
                    ? <td className="dc">{fmtD(row._start)}</td>
                    : (edit && edit.taskId === row.id && edit.field === 'start'
                        ? <td className="dc"><input autoFocus className="ie-input" value={edit.value}
                            onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                            onBlur={commitEdit}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); else if (e.key === 'Escape') cancelEdit(); }} /></td>
                        : <td className="dc ed" onClick={(e) => { if (e.ctrlKey || e.metaKey || e.shiftKey) return; startEdit(row.id, 'start', fmtD(row._start).replace(/^\w+\s/, '')); }}>
                            {(row.taskMode || 'AUTO') === 'AUTO' && (
                              <span className="auto-start-lock" role="img"
                                aria-label="Auto-computed start"
                                title="Computed from predecessor; switch to MANUAL to edit">&#x1F512;</span>
                            )}
                            {fmtD(row._start)}
                          </td>)
                  }
                  {/* Finish — editable for all leaf tasks (PL-S1; AUTO recomputes Duration). WBS-RETIRE: WBS branch removed. */}
                  {sm
                    ? <td className="dc">{fmtD(row._finish)}</td>
                    : (edit && edit.taskId === row.id && edit.field === 'finish'
                        ? <td className="dc"><input autoFocus className="ie-input" value={edit.value}
                            onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                            onBlur={commitEdit}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); else if (e.key === 'Escape') cancelEdit(); }} /></td>
                        : <td className="dc ed" onClick={(e) => { if (e.ctrlKey || e.metaKey || e.shiftKey) return; startEdit(row.id, 'finish', fmtD(row._finish).replace(/^\w+\s/, '')); }}>{fmtD(row._finish)}</td>)
                  }
                  {/* Predecessor. WBS-RETIRE: WBS branch removed. */}
                  {sm
                    ? <td className="pc zero"></td>
                    : (edit && edit.taskId === row.id && edit.field === 'predecessor'
                        ? <td className="pc"><input autoFocus className="ie-input center" value={edit.value}
                            data-testid={`pdp-pred-input-${row.id}`}
                            onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                            onBlur={commitEdit}
                            onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); else if (e.key === 'Escape') cancelEdit(); }} /></td>
                        : <td className="pc ed" data-testid={`pdp-pred-cell-${row.id}`} onClick={(e) => {
                            if (e.ctrlKey || e.metaKey || e.shiftKey) return;
                            // PRED-DISPLAY-BY-ROW-S1 — seed the editor
                            // with the predecessor's ROW NUMBER (# ordinal),
                            // not the raw SortOrder.
                            const j = row.predecessorSortOrder
                              ? rows.findIndex((r) => r.sortOrder === row.predecessorSortOrder)
                              : -1;
                            startEdit(row.id, 'predecessor', j >= 0 ? String(j + 1) : '');
                          }}>{(() => {
                            // PRED-DISPLAY-BY-ROW-S1 — render the predecessor's
                            // ROW NUMBER so the value matches the '#' column
                            // regardless of SortOrder gaps. Dangling refs →
                            // blank (nothing to point at).
                            if (!row.predecessorSortOrder) return '';
                            const j = rows.findIndex((r) => r.sortOrder === row.predecessorSortOrder);
                            return j >= 0 ? j + 1 : '';
                          })()}</td>)
                  }
                  {/* TS-S1: Task Status. NF-S5 — WBS rows in read-only
                      view get a Done checkbox that PATCHes immediately
                      via patchWbsTaskState. PM view still uses the same
                      status dropdown on WBS rows. */}
                  {/* WBS-RETIRE — the readOnly-WBS Done-checkbox
                      branch is gone with the mode; every row now
                      uses the status dropdown. */}
                  {sm
                    ? <td className="st" />
                    : <td className="st" style={{ width: effortColWidths.status }}>
                        <span
                          className={`mi ${(row.status || 'NotStarted') === 'Completed' ? 'st-c' : (row.status === 'InProgress' ? 'st-i' : 'st-n')}`}
                          onClick={(e) => openStatusDrop(e, row.id)}
                          title={row.status || 'NotStarted'}
                        >
                          {(row.status || 'NotStarted').charAt(0)}
                        </span>
                      </td>
                  }
                  {/* Roles: hours + cost € per role */}
                  {roles.map((role) => {
                    const h = row.roles?.[role.id]?.plannedHours || 0;
                    // RA-S5 — fill ratio / drawer-active class dropped
                    // with the assignment layer (no per-person plan
                    // anymore; consumed hours live in reports).
                    const cost = sumCost(rows, i, role, amountMode === 'sell');
                    // OFF-BUDGET-TASK-S1 — on an off-budget LEAF in SELL
                    // view, the cell shows the role-rate value as a
                    // CLAIMABLE amount (muted/italic) even though it's
                    // excluded from the SELL rollup above.
                    const sellViewActive = amountMode === 'sell';
                    const showClaimable = sellViewActive && row.offBudget && !row.isSummaryDerived;
                    const cellH = row.roles?.[role.id]?.plannedHours || 0;
                    const displayCost = showClaimable
                      ? cellH * (role.sellRatePerHour || 0)
                      : cost;
                    const extraCellCls = showClaimable ? ' sell-claimable' : '';
                    const sumH = sumHours(rows, i, role);
                    const hField = `role_${role.id}`;
                    const isHEdit = edit && edit.taskId === row.id && edit.field === hField;
                    const cellFillStyle = undefined;
                    const ucExtraClass = '';
                    return (
                      <React.Fragment key={`r-${row.id}-${role.id}`}>
                        {sm
                          ? <td className={`uc ${sumH === 0 ? 'zero' : ''}`}>{fmtH(sumH)}</td>
                          : (isHEdit
                              ? <td className="uc"><input autoFocus className="ie-input center" value={edit.value}
                                  onChange={(e) => setEdit({ ...edit, value: e.target.value })}
                                  onBlur={commitEdit}
                                  onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); else if (e.key === 'Escape') cancelEdit(); }} /></td>
                              : (() => {
                                  // NF-S2 REV 1.5 — read-only leaf cells with
                                  // assignees show ONE compact line per person:
                                  // "К. Дончев (16h)". The standalone hours
                                  // line is dropped (it duplicates info and
                                  // wastes a row). Cells with no assignees
                                  // fall back to the total hours.
                                  const assignees = readOnly
                                    ? (row.roles?.[role.id]?.assignees || [])
                                    : [];
                                  const showNames = readOnly && assignees.length > 0;
                                  return (
                                    <td
                                      className={`uc${readOnly ? '' : ' ed'}${ucExtraClass} ${h === 0 ? 'zero' : ''}${readOnly ? ' uc-readonly' : ''}`}
                                      style={cellFillStyle}
                                      onClick={(e) => { if (e.ctrlKey || e.metaKey || e.shiftKey) return; startEdit(row.id, hField, h || ''); }}>
                                      {showNames ? (
                                        <div className="pdp-cell-assignees" data-testid={`pdp-assignees-${row.id}-${role.id}`}>
                                          {assignees.map((a, idx) => {
                                            // REV 1.6 — never render empty "()".
                                            // Per-assignee display hours: the
                                            // assignment's own hours if > 0;
                                            // otherwise the cell's planned
                                            // hours when there's only ONE
                                            // assignee (sensible inference);
                                            // otherwise nothing.
                                            const ah = Number(a.assignedHours) || 0;
                                            const displayH = ah > 0
                                              ? ah
                                              : (assignees.length === 1 && h > 0 ? h : 0);
                                            return (
                                              <div key={idx} className="pdp-assignee">
                                                {abbrevName(a.employeeFullName)}
                                                {displayH > 0 && (
                                                  <>
                                                    {' '}
                                                    <span className="pdp-assignee-h">({fmtH(displayH)})</span>
                                                  </>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      ) : fmtH(h)}
                                    </td>
                                  );
                                })())
                        }
                        {!readOnly && (
                          // RA-S5 — cost cell is display-only now. The
                          // click-to-open-assignment-drawer + hover
                          // tooltip both retired with the assignment UI.
                          <td
                            data-testid={`cost-cell-${row.id}-${role.id}`}
                            className={`cc ${displayCost === 0 ? 'zero' : ''}${extraCellCls}`}
                          >
                            {displayCost > 0 ? fmtC(displayCost) : ''}
                          </td>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {/* Total */}
                  {(() => {
                    const totH = sumHoursAllRoles(rows, i, roles);
                    return <td className="tch">{fmtH(totH)}</td>;
                  })()}
                  {!readOnly && (
                    <td className="tc">{fmtC(rowTotal(rows, i, roles, amountMode === 'sell'))}</td>
                  )}
                </tr>
              );
            })}
            {/* E-S5: ghost row — click to add a task. Hidden in read-only mode. */}
            {!readOnly && (
              <tr className="task-ghost-row" data-testid="task-ghost-row" onClick={handleGhostRowClick}>
                <td className="rn">{rows.length + 1}</td>
                <td className="tm" />
                <td className="tn ghost-name">
                  <span className="ghost-placeholder">+ Click to add task…</span>
                </td>
                <td className="du" />
                <td className="dc" />
                <td className="dc" />
                <td className="pc" />
                <td className="st" />
                {roles.map((role) => (
                  <React.Fragment key={`ghost-${role.id}`}>
                    <td className="uc" />
                    <td className="cc" />
                  </React.Fragment>
                ))}
                <td className="tch" />
                <td className="tc" />
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      <div className="status-bar">
        <span>Tasks: <strong>{rows.filter((r) => !r.isSummaryDerived).length}</strong></span>
        <span>Summaries: <strong>{rows.filter((r) => r.isSummaryDerived).length}</strong></span>
        <span>Roles: <strong>{roles.length}</strong></span>
        <span className="right">{selTask ? `Selected: ${selTask.name}` : 'No selection'}</span>
      </div>

      {/* Context menu */}
      {ctx.open && (
        <div className="ctx" ref={ctxRef} style={{ left: ctx.x, top: ctx.y }} onClick={(e) => e.stopPropagation()}>
          {!readOnly && <div className="ci" onClick={insertTaskAboveClick}>Insert Task Above</div>}
          {!readOnly && <div className="ci" onClick={insertTaskBelowClick}>Insert Task Below</div>}
          {/* WBS-FIX-S1 — "Add WBS task" (renamed from "Add Child"). The
              created child is ALWAYS WBS; the parent stays a costing-leaf
              (planUtils.buildRows treats hosts of only-WBS children as
              leaves). Single-select only; disabled when the selected row
              is itself WBS (a WBS task can't be a parent).
              ASGN-S3 §A.2 — the readOnly branch was gone. WBS-RETIRE:
              the PM branch is gone too — no code path creates WBS
              rows anymore. */}
          {/* NF-S5 — Task Details… drawer for Description + Notes. */}
          <div
            className={`ci${selectedIds.length !== 1 ? ' ci-dis' : ''}`}
            data-testid="ctx-task-details"
            onClick={selectedIds.length === 1 ? (() => { setCtx((c) => ({ ...c, open: false })); openTaskDetailsFor(selectedIds[0]); }) : undefined}
          >
            Task Details…
          </div>
          {/* OFF-BUDGET-TASK-S1 — toggle off-budget on the right-clicked
              row. PM only (!readOnly). The consultant cannot set the
              flag (display-only). Shown for any non-WBS row, including
              summaries (a summary going off-budget is unusual but
              allowed — the rollup just contributes 0 to SELL). */}
          {!readOnly && (() => {
            const sel = selectedIds.length === 1
              ? rows.find((r) => r.id === selectedIds[0])
              : null;
            if (!sel) return null;
            return (
              <div
                className="ci"
                data-testid="ctx-off-budget"
                onClick={() => {
                  setCtx((c) => ({ ...c, open: false }));
                  dispatch(updateTaskField(sel.id, 'offBudget', !sel.offBudget));
                }}
              >
                {sel.offBudget ? '✓ ' : ''}Off-budget (CR)
              </div>
            );
          })()}
          {/* ASGN-S3 §A.2 — the consultant Delete WBS entry is
              removed; the Plan is view-only. PM Delete lives in the
              main toolbar (Delete button) unchanged. */}
          {/* WBS-RETIRE — Focus colour submenu is gone with the
              WBS-only FocusFlag column. */}
          {!readOnly && <div className="cs"></div>}
          {!readOnly && <div className="ci" onClick={doIndentClick}>Indent<span className="sc">Tab</span></div>}
          {!readOnly && <div className="ci" onClick={doOutdentClick}>Outdent<span className="sc">Shift+Tab</span></div>}
          {!readOnly && <div className="cs"></div>}
          {!readOnly && <div className="ci" onClick={() => { setCtx((c) => ({ ...c, open: false })); doMoveClick(-1); }}>Move Up</div>}
          {!readOnly && <div className="ci" onClick={() => { setCtx((c) => ({ ...c, open: false })); doMoveClick(1); }}>Move Down</div>}
          {!readOnly && <div className="cs"></div>}
          {!readOnly && <div className="ci" onClick={deleteSelectedClick}>Delete<span className="sc">Del</span></div>}
        </div>
      )}

      {/* Task mode dropdown */}
      {modeDrop.open && (
        <div className="mdrop" ref={modeDropRef} style={{ left: modeDrop.x, top: modeDrop.y }} onClick={(e) => e.stopPropagation()}>
          <div className="mo" onClick={() => setMode('AUTO')}><span className="dt" style={{ background: '#5b9bd5' }}>A</span>Auto Scheduled</div>
          <div className="mo" onClick={() => setMode('MANUAL')}><span className="dt" style={{ background: '#ed7d31' }}>M</span>Manually Scheduled</div>
          {/* WI-S5 §2.3 forward guard — WBS is retired; use work items
              on a costed task instead. Server rejects WBS on create. */}
        </div>
      )}
      {statusDrop.open && (
        <div className="mdrop" ref={statusDropRef} style={{ left: statusDrop.x, top: statusDrop.y }} onClick={(e) => e.stopPropagation()}>
          <div className="mo" onClick={() => setTaskStatus(statusDrop.taskId, 'NotStarted')}><span className="dt" style={{ background: '#9e9e9e' }}>N</span>Not Started</div>
          <div className="mo" onClick={() => setTaskStatus(statusDrop.taskId, 'InProgress')}><span className="dt" style={{ background: '#1976d2' }}>I</span>In Progress</div>
          <div className="mo" onClick={() => setTaskStatus(statusDrop.taskId, 'Completed')}><span className="dt" style={{ background: '#43a047' }}>C</span>Completed</div>
        </div>
      )}

      {/* Role dialog — dual-panel picker */}
      {roleDialog.open && (
        <div className="rd-overlay" onClick={closeRoleDialog}>
          <div className="rd-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="rd-header">
              <h2>Manage Project Roles</h2>
              <button className="rd-close" onClick={closeRoleDialog} title="Close">×</button>
            </div>

            {rdView === 'panels' && (
              <>
                <div className="rd-body">
                  <div className="rd-panels">
                    <div className="rd-panel available">
                      <div className="rd-panel-title">
                        Available Roles
                        <span className="rd-count">{rdAvailable.length}</span>
                      </div>
                      <div className="rd-panel-list">
                        {rdAvailable.length === 0 ? (
                          <div className="rd-empty">All roles added to project</div>
                        ) : rdAvailable.map((r, i) => (
                          <div key={`l-${r.abbr}-${i}`}
                            className={`rd-role-row${i === rdSelLeft ? ' selected' : ''}`}
                            onClick={() => { setRdSelLeft(i); setRdSelRight(-1); }}
                            onDoubleClick={() => { setRdSelLeft(i); setRdSelRight(-1); setTimeout(rdMoveRight, 0); }}>
                            <span className="rd-role-abbr">{r.abbr}</span>
                            <div className="rd-role-info">
                              <div className="rd-role-name">{r.name}{r.isCustom && <span className="rd-custom-badge">CUSTOM</span>}</div>
                              <div className="rd-role-rates">Sell: {r.sell.toFixed(2)} €/h &nbsp;|&nbsp; Cost: {r.cost.toFixed(2)} €/h</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rd-arrows">
                      <button className="add" title="Add to project (→)" onClick={rdMoveRight} disabled={rdSelLeft < 0}>→</button>
                      <button className="add" title="Add all (⇉)" onClick={rdMoveAllRight} disabled={rdAvailable.length === 0}>⇉</button>
                      <button className="remove" title="Remove from project (←)" onClick={rdMoveLeft} disabled={rdSelRight < 0}>←</button>
                      <button className="remove" title="Remove all (⇇)" onClick={rdMoveAllLeft} disabled={rdProject.length === 0}>⇇</button>
                    </div>

                    <div className="rd-panel project">
                      <div className="rd-panel-title">
                        Project Roles
                        <span className="rd-count">{rdProject.length}</span>
                      </div>
                      <div className="rd-panel-list">
                        {rdProject.length === 0 ? (
                          <div className="rd-empty">No roles in project yet — add from the left panel</div>
                        ) : rdProject.map((r, i) => (
                          <div key={`r-${r.abbr}-${i}`}
                            className={`rd-role-row${i === rdSelRight ? ' selected' : ''}`}
                            onClick={() => { setRdSelRight(i); setRdSelLeft(-1); }}
                            onDoubleClick={() => { setRdSelRight(i); setRdSelLeft(-1); setTimeout(rdMoveLeft, 0); }}>
                            <span className="rd-role-abbr">{r.abbr}</span>
                            <div className="rd-role-info">
                              <div className="rd-role-name">{r.name}{r.isCustom && <span className="rd-custom-badge">CUSTOM</span>}</div>
                              <div className="rd-role-rates">Sell: {r.sell.toFixed(2)} €/h &nbsp;|&nbsp; Cost: {r.cost.toFixed(2)} €/h</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rd-footer">
                  <div className="rd-footer-left">
                    <button className="rd-btn accent" onClick={rdOpenNewForm}>+ New Custom Role</button>
                    <button className="rd-btn edit-btn" onClick={rdOpenEditForm} disabled={rdSelLeft < 0 && rdSelRight < 0}>✎ Edit Role</button>
                  </div>
                  <div className="rd-footer-right">
                    <button className="rd-btn" onClick={closeRoleDialog}>Cancel</button>
                    <button className="rd-btn primary" onClick={rdSaveChanges}>Save Changes</button>
                  </div>
                </div>
                <div className="rd-status">Click a role to select it, then use → or ← to move between panels.</div>
              </>
            )}

            {rdView === 'form' && (
              <>
                <div className="rd-body">
                  <div className="rd-form-view">
                    <h4 className="rd-form-title">
                      {rdEditMode === null
                        ? 'New Custom Role'
                        : <>Edit Role: <span className="rd-edit-label">{rdForm.abbr} — {rdForm.name}</span></>}
                    </h4>
                    <div className="rd-form-grid">
                      <div className="rd-field">
                        <label>Abbreviation (max 4 letters)</label>
                        <input maxLength={4} placeholder="e.g. DBA"
                          value={rdForm.abbr}
                          disabled={rdEditMode !== null}
                          onChange={(e) => setRdForm((f) => ({ ...f, abbr: e.target.value }))} />
                      </div>
                      <div className="rd-field">
                        <label>Full Name</label>
                        <input placeholder="e.g. Database Administrator" maxLength={100}
                          value={rdForm.name}
                          onChange={(e) => setRdForm((f) => ({ ...f, name: e.target.value }))} />
                      </div>
                      <div className="rd-field">
                        <label>Sell Rate (€/h)</label>
                        <input type="number" min="0" step="5" placeholder="70"
                          value={rdForm.sell}
                          onChange={(e) => setRdForm((f) => ({ ...f, sell: e.target.value }))} />
                      </div>
                      <div className="rd-field">
                        <label>Cost Rate (€/h)</label>
                        <input type="number" min="0" step="5" placeholder="35"
                          value={rdForm.cost}
                          onChange={(e) => setRdForm((f) => ({ ...f, cost: e.target.value }))} />
                      </div>
                    </div>
                    {rdForm.err && <div className="rd-form-err">{String(rdForm.err)}</div>}
                  </div>
                </div>
                <div className="rd-footer">
                  <div></div>
                  <div className="rd-footer-right">
                    <button className="rd-btn" onClick={rdBackToPanels}>← Back</button>
                    <button className="rd-btn primary" onClick={rdSubmitForm}>Save</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* NF-S5 — Task Details drawer (Description + Notes). PM view
          edits both via the session (batch Save persists); read-only
          view shows Description read-only and PATCHes Notes via the
          new endpoint on Apply. */}
      {taskDetails && (
        <div
          className="ux-confirm-backdrop"
          data-testid="task-details-backdrop"
          onClick={() => setTaskDetails(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
          }}
        >
          <div
            className="ux-confirm-dialog"
            data-testid="task-details-dialog"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 8, minWidth: 480, maxWidth: '90vw',
              boxShadow: '0 10px 40px rgba(15,23,42,0.25)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb' }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>Task Details — {taskDetails.name}</h3>
            </div>
            <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* WBS-RETIRE — the readOnly-WBS Name-input branch
                  is gone; Name is edited inline from the grid
                  (PM only). */}
              {/* WBS-RETIRE — Description is read-only for every
                  row now (was editable on WBS in PM mode). PM edits
                  live via the Notes field below. */}
              {(() => {
                const descReadOnly = true;
                return (
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                    Description
                    <textarea
                      data-testid="task-details-description"
                      value={taskDetails.description}
                      maxLength={2000}
                      readOnly={descReadOnly}
                      onChange={(e) => setTaskDetails((cur) => cur ? { ...cur, description: e.target.value } : cur)}
                      style={{
                        minHeight: 80, fontFamily: 'inherit', fontSize: 12, padding: 6, resize: 'vertical',
                        background: descReadOnly ? '#f1f5f9' : undefined,
                        color: descReadOnly ? '#475569' : undefined,
                      }}
                    />
                    <span data-testid="task-details-description-counter" style={{ alignSelf: 'flex-end', fontSize: 11, color: '#64748b' }}>
                      {taskDetails.description.length} / 2000
                    </span>
                  </label>
                );
              })()}
              {/* ASGN-S3 §A.2 — Notes is read-only in the consultant
                  view. PM keeps the editable field. */}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                Notes
                <textarea
                  data-testid="task-details-notes"
                  value={taskDetails.notes}
                  maxLength={2000}
                  readOnly={readOnly}
                  onChange={(e) => setTaskDetails((cur) => cur ? { ...cur, notes: e.target.value } : cur)}
                  style={{
                    minHeight: 80, fontFamily: 'inherit', fontSize: 12, padding: 6, resize: 'vertical',
                    background: readOnly ? '#f1f5f9' : undefined,
                    color: readOnly ? '#475569' : undefined,
                  }}
                />
                <span data-testid="task-details-notes-counter" style={{ alignSelf: 'flex-end', fontSize: 11, color: '#64748b' }}>
                  {taskDetails.notes.length} / 2000
                </span>
              </label>
              {/* OFF-BUDGET-TASK-S1 — PM-only checkbox. The flag dispatches
                  directly into the session (separate from the drawer's
                  Apply step since it's a single boolean and the user
                  expects an immediate visual change in the grid card).
                  In readOnly the checkbox is rendered disabled. */}
              {(() => {
                const liveRow = rows.find((r) => r.id === taskDetails.taskId);
                const isOff = !!liveRow?.offBudget;
                return (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <input
                      type="checkbox"
                      data-testid="task-details-offbudget"
                      checked={isOff}
                      disabled={readOnly}
                      onChange={(e) => {
                        if (readOnly) return;
                        dispatch(updateTaskField(taskDetails.taskId, 'offBudget', e.target.checked));
                      }}
                    />
                    Off-budget (за CR)
                  </label>
                );
              })()}
            </div>
            <div style={{ padding: '12px 18px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              {/* ASGN-S3 §A.2 — in readOnly the Apply button is
                  hidden (nothing to apply) and Cancel is relabelled
                  Close so the semantics match the view-only shape. */}
              <button
                type="button"
                data-testid="task-details-cancel"
                onClick={() => setTaskDetails(null)}
                style={{ padding: '6px 14px', background: '#fff', color: '#1f2937', border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
              >{readOnly ? 'Close' : 'Cancel'}</button>
              {!readOnly && (
                <button
                  type="button"
                  data-testid="task-details-apply"
                  onClick={applyTaskDetails}
                  style={{ padding: '6px 14px', background: '#0f172a', color: '#fff', border: '1px solid #0f172a', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}
                >Apply</button>
              )}
            </div>
          </div>
        </div>
      )}

      {dataRouter && <NavigationBlocker active={dirty} onBlocked={onBlocked} />}

      <UnsavedChangesDialog
        open={showDialog}
        dirtyCount={dCount}
        onSave={handleDialogSave}
        onDiscard={handleDialogDiscard}
        onCancel={handleDialogCancel}
      />

      {conflict && (
        <div className="unsaved-overlay" role="dialog" aria-modal="true" data-testid="conflict-dialog">
          <div className="unsaved-dialog">
            <h3>Project changed elsewhere</h3>
            <p>
              This project was modified in another session. Reload to get the
              latest version — your unsaved changes will be lost.
            </p>
            <div className="unsaved-actions">
              <button className="btn btn-danger" onClick={handleConflictReload}>
                Reload (lose changes)
              </button>
              <button className="btn" onClick={handleConflictCancel}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {snack.msg && <div className={`snack ${snack.err ? 'err' : ''}`}>{snack.msg}</div>}

      {/* RA-S5 — assignment + manage-assignments drawers and the
          per-cell hover tooltip retired with the whole assignment
          layer. Only the ManageTeamDrawer (consultant roster) stays. */}
      {!readOnly && (
        <ManageTeamDrawer
          open={manageTeamOpen}
          projectId={projectId}
          projectName={project?.name || ''}
          onClose={() => setManageTeamOpen(false)}
        />
      )}

      {effortPrefs && (
        <EffortGridPrefs
          open={prefsOpen}
          anchorRef={prefsAnchorRef}
          prefs={effortPrefs}
          onChange={(next) => { setEffortPrefs(next); saveEffortPrefs(next); }}
          onClose={() => setPrefsOpen(false)}
        />
      )}
    </div>
  );
};

// NF-S2 (REVISION 1.2) — outer dispatcher. `readOnly` is threaded into the
// SAME page + SAME grid component the PM uses; there is no parallel viewer.
// readOnly=false renders the PM path byte-for-byte unchanged.
const ProjectDetailPage = ({ readOnly = false }) =>
  <ProjectDetailManagePage readOnly={readOnly} />;

export default ProjectDetailPage;
